import { useState } from 'react';
import { parseAsString, useQueryState } from 'nuqs';
import { toast } from 'sonner';
import type { PaymentDto } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { FormModal } from '@/components/shared/form-modal';
import { Field } from '@/components/shared/field';
import { useTableParams } from '@/components/shared/use-table-params';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { errorMessage } from '@/lib/api';
import { fmtDateTime, money } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import { useRefundPayment, usePayments } from '../api';
import { Pill } from '../pill';

function RefundModal({ payment, onClose }: { payment: PaymentDto; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const mut = useRefundPayment();
  const invalid = reason.trim().length < 3;

  async function refund() {
    try {
      await mut.mutateAsync({ id: payment.id, reason: reason.trim() });
      toast.success('Recebimento estornado.');
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      size="sm"
      title="Estornar recebimento"
      description={`${payment.customerName ?? 'Cliente avulso'} · ${money(payment.totalCents)}. O estorno exige um caixa aberto na unidade.`}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Voltar
          </Button>
          <ConfirmDialog
            trigger={<Button variant="destructive" disabled={invalid || mut.isPending}>Estornar recebimento</Button>}
            title="Estornar este recebimento?"
            description="O valor sai do caixa, a comissão gerada é estornada e o atendimento volta para as pendências. Essa ação não pode ser desfeita."
            confirmLabel="Estornar"
            onConfirm={refund}
          />
        </>
      }
    >
      <Field id="refund-reason" label="Motivo do estorno" hint="Mínimo de 3 caracteres.">
        <Textarea id="refund-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} rows={3} />
      </Field>
    </FormModal>
  );
}

export function PaymentsTable({ branchId }: { branchId: string }) {
  const tz = useTimezone();
  const { can } = useSession();
  const params = useTableParams();
  const [status, setStatus] = useQueryState('situacao', parseAsString.withOptions({ history: 'replace' }));
  const q = usePayments({ ...params.query, branchId, status });
  const [refunding, setRefunding] = useState<PaymentDto | null>(null);

  const columns: DataColumn<PaymentDto>[] = [
    { header: 'Data', accessorFn: (r) => r.createdAt, cell: ({ row }) => <span className="tabular">{fmtDateTime(row.original.createdAt, tz)}</span> },
    { header: 'Cliente', accessorFn: (r) => r.customerName, cell: ({ row }) => row.original.customerName ?? 'Avulso' },
    { header: 'Referente a', accessorFn: (r) => r.serviceName, cell: ({ row }) => row.original.serviceName ?? (row.original.subscriptionId ? 'Assinatura' : '-') },
    { header: 'Métodos', id: 'methods', cell: ({ row }) => row.original.items.map((i) => i.methodName).join(', ') || 'Sem cobrança' },
    { header: 'Total', accessorFn: (r) => r.totalCents, cell: ({ row }) => money(row.original.totalCents), meta: { align: 'right' } },
    {
      header: 'Situação',
      id: 'status',
      cell: ({ row }) =>
        row.original.status === 'refunded' ? (
          <span title={row.original.refundReason ?? undefined}>
            <Pill tone="bad">Estornado</Pill>
          </span>
        ) : (
          <Pill tone="good">Confirmado</Pill>
        ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      cell: ({ row }) =>
        can('cash.manage') && row.original.status === 'confirmed' ? (
          <Button size="sm" variant="outline" onClick={() => setRefunding(row.original)}>
            Estornar
          </Button>
        ) : null,
      meta: { align: 'right' },
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={q.data}
        params={params}
        isLoading={q.isLoading}
        error={q.error}
        onRetry={() => q.refetch()}
        searchPlaceholder="Buscar por cliente"
        empty={{ title: 'Nenhum recebimento registrado', description: 'Os recebimentos feitos no caixa aparecem aqui.' }}
        filters={
          <Select value={status ?? 'all'} onValueChange={(v) => { void setStatus(v === 'all' ? null : v); void params.set({ page: 1 }); }}>
            <SelectTrigger aria-label="Situação" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as situações</SelectItem>
              <SelectItem value="confirmed">Confirmados</SelectItem>
              <SelectItem value="refunded">Estornados</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      {refunding && <RefundModal payment={refunding} onClose={() => setRefunding(null)} />}
    </>
  );
}
